/* eslint-disable indent */
/* eslint-disable react/no-array-index-key */
import Label from '@/components/Label';
import ReactViewer from '@/components/ReactViewer';
import { useLocales } from '@/Locales';
import OcrImage from '@/pages/patient/detail/components/OcrImage';
import useImageSort from '@/pages/patient/hooks/useImageSort';
import { httpImageRecognitionFind } from '@/services/imageProcess';
import { previewFile } from '@/utils';
import { decrypt } from '@/utils/sm-crypto';
import { Button } from '@sinohealth/butterfly-ui-components';
import _ from 'lodash';
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { ImageDecorator } from 'react-viewer/lib/ViewerProps';
import Sortable from 'sortablejs';
import { OcrContext } from '../PatientTabs/components/TabItem';
import TextinSingleCompress from '../TextinSingleCompress';
import styles from './index.less';

const RecordImgUpload = forwardRef((props: any, ref: any) => {
  const {
    value,
    originUrl, // 接口返回的图片地址
    recordId,
    onChange,
    disabled,
    duration,
    onEdit,
    label = '报告图片',
    accept = 'image/jpeg,image/jpg,image/bmp,image/png',
    fileType,
  } = props;
  const { t } = useLocales();
  const [params] = useSearchParams();
  const patientId = decrypt(params.get('id') || params.get('patientId') || '');
  const [images, setImages] = useState<string[]>([]);
  const [imageToIndex, setImageToIndex] = useState<any[]>([]);
  const {
    loading,
    recognitTaskId,
    finished,
    setFilePaths,
    setReportText,
    setOcrDetail,
    setOcrConfig,
  } = useContext(OcrContext);
  // 所有的预览地址
  const [viewImages, setViewImages] = useState<ImageDecorator[]>([]);
  const [viewImagesIndex, setViewImagesIndex] = useState(0);
  const [hasVideo, setHasVideo] = useState(false); // 是否有视频[video]
  const [canOcrFlag, setCanOcrFlag] = useState(true); // 是否可以点击ocr识别
  const sortDargRef = useRef<any>(null);
  const singleCompressRef = useRef<any>(null);

  const { sortLoading, setImageSortParams } = useImageSort();

  useImperativeHandle(ref, () => ({
    loading: () => singleCompressRef?.current?.loading(),
  }));

  useEffect(() => {
    setImages(value ? value.split(',') : []);
    setViewImagesIndex(0);
  }, [value]);

  useMemo(() => {
    setImageToIndex(
      originUrl
        ? originUrl.split(',')?.map((el: any, i: number) => {
            return {
              index: i + 1,
              sort: i,
              url: el,
            };
          })
        : [],
    );
    setHasVideo(
      originUrl
        ? originUrl.split(',').some((el: string | string[]) => el.indexOf('/video') > -1)
        : false,
    );
  }, [originUrl]);

  useEffect(() => {
    const sortI = imageToIndex;
    // console.log(sortI);
    onChange?.(sortI.map((el) => el.url).join(','));
  }, [imageToIndex]);

  const onUploadSuccess = (info?: string | string[], index?: number) => {
    // const imgVar = info.file?.response?.data;
    if (!info) return;
    // 视频上传
    if (Array.isArray(info)) {
      onChange([...images, ...info].join(','));
      setImageToIndex((prev) => {
        // 取最大值
        const sort = imageToIndex.length ? Math.max(...imageToIndex.map((item) => item.sort)) : 0;
        const videos = info.map((el) => {
          return { index: sort + 1, sort: sort + 1, url: el };
        });
        const newImageToIndex = [...prev, ...videos];
        newImageToIndex.sort((a, b) => a.sort - b.sort);
        return newImageToIndex;
      });
      setFilePaths([...images, ...info].join(','));
    } else {
      onChange([...images, info].join(','));
      // 新增图片后需要获取图片的ocr结果
      setFilePaths([...images, info].join(','));
      setImageToIndex((prev) => {
        const newImageToIndex = [...prev, { index, sort: index, url: info }];
        newImageToIndex.sort((a, b) => a.sort - b.sort);
        return newImageToIndex;
      });
    }
  };

  const handleOnDelete = (imgSrc: string, index: number) => {
    const imgs = [...images];
    // .filter((item) => item !== imgSrc);
    imgs.splice(index, 1);
    const imgsIndex = [...imageToIndex];
    imgsIndex.splice(index, 1);
    setImageToIndex(imgsIndex);
    // console.log({ imgs, index });
    onChange(imgs.join(','));
    // 新增图片后需要获取图片的ocr结果
    setFilePaths(imgs.join(','));
  };

  const handleOnUpdate = (index: number, imgSrc: string) => {
    const imgs = [...images];
    // imgs[index] = imgSrc; // 替换为新的图片地址[imgSrc]
    imgs.splice(index, 1, imgSrc);
    const imgsIndex = [...imageToIndex];
    const obj = {
      index: imgsIndex[index].index,
      sort: imgsIndex[index].sort,
      url: imgSrc,
    };
    imgsIndex.splice(index, 1, obj);
    setImageToIndex(imgsIndex);
    onChange(imgs.join(','));
  };

  const handleOnStartOcr = async () => {
    if (singleCompressRef?.current?.loading()) return;
    try {
      const result: any = await httpImageRecognitionFind({
        patientId,
        taskType: 'single', // 单模块:single,多模块:multi
        moduleType: fileType || undefined,
        recordId,
      });
      startOcrFn(recognitTaskId || result?.id);
    } catch (error) {
      console.log('error', error);
    }
  };

  const startOcrFn = (taskId: string) => {
    const suffix = ['.jpg', '.jpeg', '.png', '.bmp'];
    setOcrDetail([]);
    setReportText('');
    setOcrConfig({
      open: true,
      data: {
        patientId,
        reportType: fileType,
        recordId,
        recognitionTaskId: taskId,
        filePaths:
          fileType === 'imageReport'
            ? images.filter((el) => suffix.some((suff) => el.toLowerCase().endsWith(suff)))
            : images,
      },
    });
  };

  const handleOnEnd = _.debounce(() => {
    const list: any[] = [];
    for (let i = 0; i < sortDargRef.current.children.length; i += 1) {
      const item = sortDargRef.current.children[i];
      if (item.id) {
        list.push(item.id);
      }
    }
    setImageToIndex(
      list.map((el, i) => {
        return {
          index: i + 1,
          sort: i,
          url: el,
        };
      }),
    );
    // console.log('handleOnEnd', list, imageToIndex);
    setImageSortParams({
      patientId,
      fileType,
      imagePaths: list,
    });
    onChange(list.join(','));
  }, 100);

  useEffect(() => {
    let sortable: any = null;
    if (images.length && sortDargRef.current && !disabled) {
      sortable = new Sortable(sortDargRef.current, {
        // handle: `.${item?.value}`,
        // sort: false, // boolean 定义是否列表单元是否可以在列表容器内进行拖拽排序
        // direction: 'vertical',
        disabled,
        group: 'shared', // 共享同一个组名，允许跨列表拖拽
        animation: 10, // ms, number 单位：ms，定义排序动画的时间
        filter: '[data-no-drag="true"]', // 禁止 data-no-drag 属性为 "true" 的元素拖拽
        delay: 0, // number 定义鼠标选中列表单元可以开始拖动的延迟时间
        ghostClass: 'sortable-ghost',
        chosenClass: styles['sortable-chosen'],
        dragClass: 'sortable-drag',
        onStart: () => {
          // setLoadingUpload(true);
        },
        onEnd: handleOnEnd,
        onMove(e: any) {
          return e.related.className.indexOf('filtered') === -1;
        },
      });
    }

    return () => {
      if (sortable) {
        sortable.destroy();
      }
    };
  }, [images, sortDargRef]);

  return (
    <div className={styles.upload}>
      <div style={{ marginBottom: label ? 12 : 0 }}>
        {label && <Label>{label}</Label>}
        {((!images?.length && !imageToIndex.length) || hasVideo) && disabled && (
          <span style={{ marginLeft: 12 }}>--</span>
        )}
        {!disabled && images.length > 0 && fileType !== 'otherInformation' && canOcrFlag && (
          <Button
            type="link"
            size="small"
            disabled={loading}
            icon={<span className="iconfont icon-ocr" style={{ marginRight: 4, fontSize: 14 }} />}
            onClick={handleOnStartOcr}
          >
            {!finished ? t('图片智能识别') : t('重新智能识别')}
          </Button>
        )}
      </div>
      {(images?.length > 0 || !disabled) && (
        <div ref={sortDargRef} className={styles['record-img']}>
          {imageToIndex?.map((item, i) => {
            return (
              item?.url?.indexOf('/video') === -1 && (
                <OcrImage
                  id={item.url}
                  key={`${item.url}_${i}`}
                  src={item.url}
                  index={i + 1}
                  request={false}
                  onDelete={(e: any) => handleOnDelete(e, i)}
                  onUpdate={(e: any) => handleOnUpdate(i, e)}
                  onEdit={onEdit}
                  disabled={disabled || loading}
                  onPreview={() => {
                    setViewImages(imageToIndex?.map((list) => ({ src: previewFile(list.url) })));
                    setViewImagesIndex(i);
                  }}
                />
              )
            );
          })}
          {!disabled && !loading && (
            <TextinSingleCompress
              ref={singleCompressRef}
              accept={accept}
              fileType={fileType}
              recordId={recordId}
              duration={duration}
              label={label}
              currentIndex={
                imageToIndex.length ? Math.max(...imageToIndex.map((item) => item.sort)) : 0
              }
              onUpdate={onUploadSuccess}
              onCanOcr={setCanOcrFlag}
            />
          )}
          {/* {!disabled && (
              <Upload
                accept={accept}
                minDimension={15}
                maxDimension={8192}
                onUploadSuccess={onUploadSuccess}
              />
            )} */}
        </div>
      )}
      {/* 自定义图片-大屏预览 */}
      <ReactViewer
        images={viewImages}
        request={false}
        activeIndex={viewImagesIndex}
        onClose={() => {
          setViewImages([]);
          setViewImagesIndex(0);
        }}
      />
    </div>
  );
});

export default RecordImgUpload;
